/**
 * Locale identity, detection and persistence — the shared source of truth for
 * which language every archlang surface renders its chrome in.
 *
 * Detection order (first hit wins): an explicit override (a plugin webview
 * passes `?locale=` from the host IDE) → the persisted choice in localStorage
 * → the browser's `navigator.language` → English. Persisting also reflects the
 * language onto `<html lang>` so the document advertises it.
 */

export type LocaleId = "en" | "ru";

/** All shippable locales, in picker order. `label` is the endonym. */
export const LOCALES: ReadonlyArray<{ id: LocaleId; label: string }> = [
  { id: "en", label: "English" },
  { id: "ru", label: "Русский" },
];

export const DEFAULT_LOCALE: LocaleId = "en";

const STORAGE_KEY = "archlang:locale";

export function isLocale(v: unknown): v is LocaleId {
  return v === "en" || v === "ru";
}

/**
 * Resolve the startup locale. `override` wins when it is a known locale (the
 * plugin-webview `?locale=` bridge); otherwise the persisted choice, then the
 * browser language, then the default.
 */
export function resolveInitialLocale(override?: string | null): LocaleId {
  if (isLocale(override)) return override;
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isLocale(saved)) return saved;
  } catch {
    /* private mode / no storage — non-fatal */
  }
  if (typeof navigator !== "undefined" && typeof navigator.language === "string") {
    const primary = navigator.language.slice(0, 2).toLowerCase();
    if (isLocale(primary)) return primary;
  }
  return DEFAULT_LOCALE;
}

/** Persist the choice and reflect it onto `<html lang>`. */
export function persistLocale(id: LocaleId): void {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* ignore */
  }
  if (typeof document !== "undefined") {
    document.documentElement.lang = id;
  }
}
